import {
  PLAYER_INTEREST_RADIUS,
  STREAM_CHUNK_SIZE,
  getLargeWorldCollisionCandidatesAt,
  getWorldHeightAt,
  getWorldRiverProfileAt,
  getWorldRoadProfileAt,
  sampleMacroBiomeAt,
  type BiomeId,
  type StaticCollider,
} from "@endclock/shared";
import type { EndclockGame } from "../game/game.js";
import type { LocalDeveloperState } from "./dev-state.js";

const BIOME_COLORS: Readonly<Record<BiomeId, string>> = Object.freeze({
  ASHLANDS: "rgba(227, 111, 47, .20)",
  GROUND_ZERO: "rgba(128, 89, 183, .24)",
  FERTILE_PRESERVE: "rgba(111, 215, 83, .22)",
  NUCLEAR_FOREST: "rgba(30, 165, 99, .22)",
  RUINED_CIVILIZATION: "rgba(229, 216, 178, .20)",
  INDUSTRIAL_RUINS: "rgba(202, 126, 132, .22)",
});

type Point = readonly [number, number];

/** One lightweight projected debug layer; it allocates nothing while disabled. */
export class DeveloperWorldRenderer {
  private context: CanvasRenderingContext2D;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly game: EndclockGame,
    private readonly state: LocalDeveloperState,
  ) {
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Developer overlay canvas is unavailable");
    this.context = context;
  }

  render(): void {
    const overlays = this.state.overlays;
    if (!overlays.worldDebug || !Object.entries(overlays).some(([key, enabled]) => key !== "worldDebug" && enabled)) {
      if (!this.canvas.hidden) {
        this.canvas.hidden = true;
        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
      }
      return;
    }
    this.canvas.hidden = false;
    this.resize();
    const context = this.context;
    context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    context.lineJoin = "round";
    context.lineCap = "round";
    const snapshot = this.game.snapshot();
    const focus = snapshot.dev.freeCam ? snapshot.dev.freeCamTarget : snapshot.player;

    if (overlays.biomeColors || overlays.biomeBoundaries) this.drawBiomes(focus.x, focus.z);
    if (overlays.chunks) this.drawChunks();
    if (overlays.roads || overlays.rivers) this.drawRoutes(focus.x, focus.z, overlays.roads, overlays.rivers);
    if (overlays.colliders) this.drawColliders(focus.x, focus.z);
    if (overlays.poi) this.drawPois();
    if (overlays.serverInterest) this.drawCircle(snapshot.player.x, snapshot.player.z, PLAYER_INTEREST_RADIUS, "#61c9ff", 1.5);
    if (overlays.interaction) this.drawInteraction(snapshot);
  }

  private resize(): void {
    const ratio = Math.min(2, devicePixelRatio || 1);
    const width = Math.max(1, Math.round(innerWidth * ratio));
    const height = Math.max(1, Math.round(innerHeight * ratio));
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
      this.canvas.style.width = `${innerWidth}px`;
      this.canvas.style.height = `${innerHeight}px`;
    }
  }

  private project(x: number, y: number, z: number): Point | undefined {
    const matrix = this.game.camera.viewProjection;
    const clipX = matrix[0]! * x + matrix[4]! * y + matrix[8]! * z + matrix[12]!;
    const clipY = matrix[1]! * x + matrix[5]! * y + matrix[9]! * z + matrix[13]!;
    const clipW = matrix[3]! * x + matrix[7]! * y + matrix[11]! * z + matrix[15]!;
    if (Math.abs(clipW) < 0.00001) return undefined;
    const normalizedX = clipX / clipW;
    const normalizedY = clipY / clipW;
    return [(normalizedX * 0.5 + 0.5) * this.canvas.width, (1 - (normalizedY * 0.5 + 0.5)) * this.canvas.height];
  }

  private line(points: readonly (readonly [number, number, number])[], color: string, width = 1, close = false): void {
    const projected = points.map(([x, y, z]) => this.project(x, y, z)).filter((point): point is Point => point !== undefined);
    if (projected.length < 2) return;
    this.context.beginPath();
    this.context.moveTo(projected[0]![0], projected[0]![1]);
    for (let index = 1; index < projected.length; index += 1) this.context.lineTo(projected[index]![0], projected[index]![1]);
    if (close) this.context.closePath();
    this.context.strokeStyle = color;
    this.context.lineWidth = width * Math.min(2, devicePixelRatio || 1);
    this.context.stroke();
  }

  private drawChunks(): void {
    const colors: Record<string, string> = {
      VISIBLE: "#e9f7cd", GPU_READY: "#6cc8ff", CPU_READY: "#d7ba62",
      REQUESTED: "#cf9453", GENERATING: "#f07d33", FAILED: "#ff3b46", EVICTABLE: "#777c78",
    };
    for (const chunk of this.game.terrain.debugChunks()) {
      const x0 = chunk.x * STREAM_CHUNK_SIZE;
      const z0 = chunk.z * STREAM_CHUNK_SIZE;
      const x1 = x0 + STREAM_CHUNK_SIZE;
      const z1 = z0 + STREAM_CHUNK_SIZE;
      this.line([
        [x0, getWorldHeightAt(x0, z0) + 0.18, z0], [x1, getWorldHeightAt(x1, z0) + 0.18, z0],
        [x1, getWorldHeightAt(x1, z1) + 0.18, z1], [x0, getWorldHeightAt(x0, z1) + 0.18, z1],
      ], colors[chunk.state] ?? "#fff", chunk.state === "VISIBLE" ? 1.6 : 0.75, true);
    }
  }

  private drawColliders(x: number, z: number): void {
    const colors = { geology: "#e7b35d", tree: "#65d880", human: "#71bfff", ai: "#e16fff" } as const;
    for (const collider of getLargeWorldCollisionCandidatesAt(x, z)) this.drawCollider(collider, colors[collider.category]);
  }

  private drawCollider(collider: StaticCollider, color: string): void {
    if (collider.shape === "circle") {
      this.drawCircle(collider.x, collider.z, collider.radius, color, 1.25);
      return;
    }
    const halfWidth = collider.shape === "capsule" ? collider.halfLength + collider.radius : collider.halfWidth;
    const halfDepth = collider.shape === "capsule" ? collider.radius : collider.halfDepth;
    const cosine = Math.cos(collider.yaw);
    const sine = Math.sin(collider.yaw);
    const points = [[-halfWidth, -halfDepth], [halfWidth, -halfDepth], [halfWidth, halfDepth], [-halfWidth, halfDepth]] as const;
    this.line(points.map(([localX, localZ]) => {
      const px = collider.x + localX * cosine + localZ * sine;
      const pz = collider.z - localX * sine + localZ * cosine;
      return [px, getWorldHeightAt(px, pz) + 0.24, pz] as const;
    }), color, 1.25, true);
  }

  private drawPois(): void {
    for (const poi of this.game.world.developerRecords().pois) {
      this.drawCircle(poi.x, poi.z, poi.footprintRadius, poi.origin === "human" ? "#49baff" : "#ef5bff", 1.5);
      this.drawCircle(poi.x, poi.z, poi.exclusionRadius, "rgba(255,255,255,.45)", 0.7);
      const point = this.project(poi.x, getWorldHeightAt(poi.x, poi.z) + 1, poi.z);
      if (point) {
        this.context.fillStyle = poi.origin === "human" ? "#bce9ff" : "#ffc8ff";
        this.context.font = `${9 * Math.min(2, devicePixelRatio || 1)}px ui-monospace, monospace`;
        this.context.fillText(`${poi.archetype} ${poi.id}`, point[0] + 4, point[1] - 4);
      }
    }
  }

  private drawInteraction(snapshot: ReturnType<EndclockGame["snapshot"]>): void {
    const interaction = snapshot.interaction;
    const origin = [snapshot.player.x, snapshot.player.y + 1.1, snapshot.player.z] as const;
    this.drawMarker(origin[0], origin[1], origin[2], 0.12, "#d7c89a");
    if (!interaction.anchor) return;
    const anchor = interaction.anchor;
    const color = interaction.lineOfSight ? "#83d8a2" : "#e57362";
    this.line([origin, anchor], color, 1.5);
    this.drawMarker(anchor[0], anchor[1], anchor[2], 0.18, color);
    if (interaction.maxRange !== null) this.drawCircle(anchor[0], anchor[2], interaction.maxRange, color, 1);
  }

  private drawMarker(x: number, y: number, z: number, radius: number, color: string): void {
    const horizontal: [number, number, number][] = [];
    const vertical: [number, number, number][] = [];
    for (let index = 0; index < 16; index += 1) {
      const angle = index / 16 * Math.PI * 2;
      horizontal.push([x + Math.cos(angle) * radius, y, z + Math.sin(angle) * radius]);
      vertical.push([x + Math.cos(angle) * radius, y + Math.sin(angle) * radius, z]);
    }
    this.line(horizontal, color, 1.4, true);
    this.line(vertical, color, 1.4, true);
  }

  private drawRoutes(x: number, z: number, roads: boolean, rivers: boolean): void {
    const radius = 190;
    const step = 8;
    if (roads) {
      const points: [number, number, number][] = [];
      for (let dz = -radius; dz <= radius; dz += step) {
        const sample = getWorldRoadProfileAt(x, z + dz);
        if (sample.distance <= radius * 1.4) points.push([sample.x, getWorldHeightAt(sample.x, sample.z) + 0.3, sample.z]);
      }
      this.line(points, "#ffd75d", 1.3);
    }
    if (rivers) {
      const points: [number, number, number][] = [];
      for (let dz = -radius; dz <= radius; dz += step) {
        const sample = getWorldRiverProfileAt(x, z + dz);
        if (sample.distance <= radius * 1.4) points.push([sample.x, getWorldHeightAt(sample.x, sample.z) + 0.35, sample.z]);
      }
      this.line(points, "#48caff", 1.5);
    }
  }

  private drawBiomes(x: number, z: number): void {
    const size = 32;
    const radius = 3;
    const centerX = Math.floor(x / size);
    const centerZ = Math.floor(z / size);
    for (let offsetZ = -radius; offsetZ <= radius; offsetZ += 1) {
      for (let offsetX = -radius; offsetX <= radius; offsetX += 1) {
        const x0 = (centerX + offsetX) * size;
        const z0 = (centerZ + offsetZ) * size;
        const x1 = x0 + size;
        const z1 = z0 + size;
        const sample = sampleMacroBiomeAt(x0 + size / 2, z0 + size / 2);
        const corners: readonly (readonly [number, number])[] = [[x0, z0], [x1, z0], [x1, z1], [x0, z1]];
        const points = corners.map(([px, pz]) => this.project(px, getWorldHeightAt(px, pz) + 0.1, pz));
        if (points.some((point) => !point)) continue;
        this.context.beginPath();
        this.context.moveTo(points[0]![0], points[0]![1]);
        for (let index = 1; index < points.length; index += 1) this.context.lineTo(points[index]![0], points[index]![1]);
        this.context.closePath();
        if (this.state.overlays.biomeColors) {
          this.context.fillStyle = BIOME_COLORS[sample.primary];
          this.context.fill();
        }
        if (this.state.overlays.biomeBoundaries && sample.distanceToBoundary < size * 1.2) {
          this.context.strokeStyle = "rgba(255,255,255,.85)";
          this.context.lineWidth = Math.min(2, devicePixelRatio || 1);
          this.context.stroke();
        }
      }
    }
  }

  private drawCircle(x: number, z: number, radius: number, color: string, width: number): void {
    const points: [number, number, number][] = [];
    for (let index = 0; index < 28; index += 1) {
      const angle = (index / 28) * Math.PI * 2;
      const px = x + Math.cos(angle) * radius;
      const pz = z + Math.sin(angle) * radius;
      points.push([px, getWorldHeightAt(px, pz) + 0.24, pz]);
    }
    this.line(points, color, width, true);
  }
}
